import { GrowthBook, useFeature } from '@growthbook/growthbook-react';
import { logPapEvent } from './pap_analytics.js';

/**
 * GrowthBook Configuration and Utilities
 * 
 * This file:
 * 1. Initializes GrowthBook SDK with the right environment
 * 2. Uses backend-provided secure public_user_id for bucketing
 *    - Backend calls get_public_id_from_user_id(user_id) 
 *    - Returns serialized customer ID (pid_eci:XXXXXX) safe for GrowthBook
 *    - No frontend hashing needed - already secure from backend
 * 3. Provides helpers for feature flag usage
 * 4. Exports custom hooks for easy feature flag checking
 */

/**
 * Get the GrowthBook client key from environment
 * @returns {string} - GrowthBook client key
 */
const getClientKey = () => {
  return process.env.GROWTHBOOK_CLIENT_KEY;
};

/**
 * Hash a secure attribute using SHA-256 (browser-compatible)  
 * 
 * NOTE: No longer used for user IDs - backend provides secure public_user_id via:
 * get_public_id_from_user_id() → serialized customer ID (pid_eci:XXXXXX)
 * 
 * This function remains for potential future use with other sensitive attributes.
 * 
 * @param {string} value - The value to hash
 * @returns {Promise<string|null>} - The hashed value (hex string)
 */
const hashSecureAttribute = async (value) => {
  if (!value) return null;
  
  const salt = process.env.GROWTHBOOK_SECURE_ATTRIBUTE_SALT;
  
  if (!salt) {
    console.error('[GrowthBook] GROWTHBOOK_SECURE_ATTRIBUTE_SALT not configured');
    return null;
  }
  
  try {
    // SHA-256 hash: salt + value (using Web Crypto API - built into browsers)
    const encoder = new TextEncoder();
    const data = encoder.encode(salt + value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    // Convert hash to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  } catch (error) {
    console.error('[GrowthBook] Failed to hash attribute:', error);
    return null;
  }
};

/**
 * Hash an array of secure attributes
 * 
 * NOTE: No longer used for user IDs - backend provides secure public_user_id.
 * This function remains for potential future use with other sensitive attribute arrays.
 * 
 * @param {string[]} values - Array of values to hash
 * @returns {Promise<string[]>} - Array of hashed values
 */
const hashSecureAttributeArray = async (values) => {
  if (!Array.isArray(values) || values.length === 0) return [];
  
  try {
    const hashedValues = await Promise.all(
      values.map(v => hashSecureAttribute(v))
    );
    return hashedValues.filter(Boolean);
  } catch (error) {
    console.error('[GrowthBook] Failed to hash attribute array:', error);
    return [];
  }
};

// REMOVED logExperimentExposure function - now handled automatically by trackingCallback!

/**
 * Get or create a session ID for tracking
 * @returns {string} - Session identifier
 */
const getSessionId = () => {
  if (typeof window !== 'undefined') {
    // Use existing session ID if available, or create one
    if (!window._sessionId) {
      window._sessionId = 'sess_' + Math.random().toString(36).substr(2, 9);
    }
    return window._sessionId;
  }
  return 'ssr_session';
};

/**
 * Create GrowthBook instance with automatic exposure tracking
 * @returns {Object|null} - GrowthBook instance or null during SSR
 */
const createGrowthBookInstance = () => {
  // Return null during SSR to prevent errors
  if (typeof window === 'undefined') {
    return null;
  }
  
  // Create a proper GrowthBook instance for React hooks
  try {
    const gb = new GrowthBook({
      apiHost: 'https://cdn.dropboxexperiment.com',
      clientKey: getClientKey(),
      attributes: {},
      enableDevMode: process.env.NODE_ENV === 'development',
      
      // AUTOMATIC EXPOSURE TRACKING - fires exactly when feature flags are evaluated
      trackingCallback: (experiment, result) => {
        // Only log your specific personalization experiment
        if (experiment.key === 'learn_personalization_2026_01_27_AB_test_feature') {
          // Skip inactive experiments - variationId is a number (0, 1, 2, etc.) not a string
          if (result.variationId === null || result.variationId === undefined) {
            return;
          }
          
          // Quick logged-out user check
          const attributes = gb.getAttributes();
          const customNumber = attributes?.custom_number;
          
          // Silent skip for logged out users
          if (!customNumber || !attributes?.loggedIn) {
            return;
          }
          
          // Map numeric variationId to string variant for PAP endpoint
          // Variation 0 (TRUE) = Control, Variation 1 (FALSE) = Treatment
          const variantMap = {
            0: 'control',
            1: 'treatment'
          };
          const variantString = variantMap[result.variationId] || `variant_${result.variationId}`;
          
          // Automatic PAP logging with proper string format
          logPapEvent(experiment.key, variantString, 'exposure');
        }
      }
    });
    
    // Store reference for attribute access
    window._growthbook = gb;
    
    return gb;
  } catch (error) {
    console.error('[GrowthBook] Failed to create instance:', error);
    // Return minimal stub as fallback
    return {
      setAttributes: () => {},
      getAttributes: () => ({}),
      destroy: () => {},
      ready: false
    };
  }
};

/**
 * Initialize GrowthBook (now works with real instance)
 * @param {Object} gbInstance - GrowthBook instance
 * @returns {Promise<void>}
 */
const initializeGrowthBook = (gbInstance) => {
  // Skip during SSR
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  
  if (!gbInstance) {
    return Promise.resolve();
  }

  try {
    // Initialize the GrowthBook instance
    return Promise.resolve();
  } catch (error) {
    console.error('[GrowthBook] Failed to initialize:', error);
    return Promise.resolve();
  }
};

/**
 * Set GrowthBook attributes (now works with real instance)
 * @param {Object} gbInstance - GrowthBook instance
 * @param {Object} personalization - User personalization attributes 
 * @returns {Promise<void>}
 */
const setGrowthBookAttributes = async (gbInstance, personalization) => {
  // Skip during SSR
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  
  if (!gbInstance || !gbInstance.setAttributes) {
    return Promise.resolve();
  }

  try {
    // Only proceed if we have valid user data - now using public_user_id from backend
    if (!personalization.public_user_id) {
      const loggedOutAttributes = {
        loggedIn: false,
        custom_number: null,
        accountType: null,
        productType: [],
        country: null
      };
      gbInstance.setAttributes(loggedOutAttributes);
      return Promise.resolve();
    }
    
    // NEW: Use public_user_id directly from backend
    // Backend generates this via get_public_id_from_user_id(user_id):
    // - Converts user_id to Customer object  
    // - Serializes customer ID to format: pid_eci:XXXXXX
    // - Already secure and obfuscated - safe for GrowthBook bucketing
    const publicUserId = personalization.public_user_id;
    
    // Set attributes on the real GrowthBook instance
    const attributes = {
      accountType: personalization.accountType || null,
      productType: personalization.productType || [],
      country: personalization.country || null,
      
      // CRITICAL: GrowthBook-specific attributes for targeting and assignment:
      custom_number: publicUserId,              // Backend-provided public customer ID (pid_eci:XXXXXX format)
      loggedIn: true                            // Targeting condition - only set true if we have public_user_id
    };
    
    gbInstance.setAttributes(attributes);
    
    // Trigger re-evaluation of experiments with new attributes
    if (gbInstance.loadFeatures) {
      await gbInstance.loadFeatures();
    }
    
    return Promise.resolve();
  } catch (error) {
    console.error('[GrowthBook] Failed to set attributes:', error);
    return Promise.resolve();
  }
};

/**
 * Wrapper for useFeature hook with SSR safety
 * @param {string} key - Feature flag key
 * @returns {any} - Feature flag value (null during SSR)
 */
export const useFeatureFlag = (key) => {
  // Return null during SSR to prevent build errors
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    return useFeature(key);
  } catch (error) {
    // Fallback if GrowthBookProvider is not available during initial load.
    return null;
  }
};

// Export all functions
export {
  getClientKey,
  hashSecureAttribute,
  hashSecureAttributeArray,
  getSessionId,
  createGrowthBookInstance,
  initializeGrowthBook,
  setGrowthBookAttributes
};
